import { useState } from "react";
import { format } from "date-fns";
import { Trash2 } from "lucide-react";

import { Button } from "@monorepo-apps/ui/src/components/button";
import { Input } from "@monorepo-apps/ui/src/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";
import { Textarea } from "@monorepo-apps/ui/src/components/textarea";

import {
  PRIORITY_LABEL,
  StatusDot,
  TASK_STATUS_LABEL,
  priorityToTone,
  taskStatusToTone,
} from "@/presentation/components/status/status-dot";
import {
  BACKLOG_VALUE,
  NO_BLOCK_VALUE,
  UNASSIGNED_VALUE,
} from "@/presentation/components/delivery-tasks/delivery-task-fields";
import { Field } from "@/presentation/components/delivery-tasks/field";
import { formatSprintRange } from "@/presentation/components/delivery-tasks/sprint-dates";
import type { DeliveryTaskPatch } from "@/infrastructure/repositories/delivery-tasks.supabase.repository";
import type {
  DeliveryTaskRow,
  Priority,
  ProfileRow,
  SprintRow,
  TaskStatus,
  WorkstreamRow,
} from "@/infrastructure/supabase/database.types";

/**
 * Full detail for one delivery task: every field editable, including the long
 * description the table row can't show. Edits are committed on blur (text) or
 * on change (selects), matching how the row behaves.
 */
export function DeliveryTaskDrawerContent({
  task,
  sprints,
  workstreams,
  assignees,
  onPatch,
  onDelete,
}: {
  task: DeliveryTaskRow;
  sprints: SprintRow[];
  workstreams: WorkstreamRow[];
  assignees: ProfileRow[];
  onPatch: (patch: DeliveryTaskPatch) => void;
  onDelete: () => void;
}) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const sprint = sprints.find((s) => s.id === task.sprint_id) ?? null;
  const block = workstreams.find((w) => w.key === task.workstream_key) ?? null;
  const assignee = assignees.find((p) => p.id === task.assignee_id) ?? null;
  const createdBy = assignees.find((p) => p.id === task.created_by) ?? null;

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <header className="border-border/60 border-b px-6 py-5">
        <p className="font-data text-muted-foreground text-[10px] tracking-[0.18em] uppercase">
          {sprint
            ? `${sprint.name} · ${formatSprintRange(sprint.start_date, sprint.end_date)}`
            : "Backlog"}
          {block ? ` · Block ${block.code}` : null}
        </p>
        <Textarea
          key={task.title}
          defaultValue={task.title}
          rows={2}
          maxLength={300}
          aria-label="Task title"
          className="mt-2 resize-none border-transparent bg-transparent px-0 text-base font-medium shadow-none focus-visible:border-transparent"
          onBlur={(e) => {
            const next = e.target.value.trim();
            if (next && next !== task.title) onPatch({ title: next });
          }}
        />
      </header>

      <div className="flex flex-col gap-5 px-6 py-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Status">
            <Select value={task.status} onValueChange={(v) => onPatch({ status: v as TaskStatus })}>
              <SelectTrigger aria-label="Status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(["todo", "in_progress", "done", "blocked"] as TaskStatus[]).map((s) => (
                  <SelectItem key={s} value={s}>
                    <span className="inline-flex items-center gap-2">
                      <StatusDot tone={taskStatusToTone(s)} /> {TASK_STATUS_LABEL[s]}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Priority">
            <Select
              value={task.priority}
              onValueChange={(v) => onPatch({ priority: v as Priority })}
            >
              <SelectTrigger aria-label="Priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(["low", "medium", "high"] as Priority[]).map((p) => (
                  <SelectItem key={p} value={p}>
                    <span className="inline-flex items-center gap-2">
                      <StatusDot tone={priorityToTone(p)} /> {PRIORITY_LABEL[p]}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Sprint">
            <Select
              value={task.sprint_id ?? BACKLOG_VALUE}
              onValueChange={(v) => onPatch({ sprint_id: v === BACKLOG_VALUE ? null : v })}
            >
              <SelectTrigger aria-label="Sprint">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={BACKLOG_VALUE}>Backlog (no sprint)</SelectItem>
                {sprints.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                    <span className="text-muted-foreground ml-1.5 text-[10px]">
                      {formatSprintRange(s.start_date, s.end_date)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Block (optional)">
            <Select
              value={task.workstream_key ?? NO_BLOCK_VALUE}
              onValueChange={(v) => onPatch({ workstream_key: v === NO_BLOCK_VALUE ? null : v })}
            >
              <SelectTrigger aria-label="Block">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_BLOCK_VALUE}>No block</SelectItem>
                {workstreams.map((w) => (
                  <SelectItem key={w.key} value={w.key}>
                    <span className="font-data text-muted-foreground text-[10px] tracking-wider uppercase">
                      {w.code}
                    </span>{" "}
                    {w.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Assignee">
            <Select
              value={task.assignee_id ?? UNASSIGNED_VALUE}
              onValueChange={(v) => onPatch({ assignee_id: v === UNASSIGNED_VALUE ? null : v })}
            >
              <SelectTrigger aria-label="Assignee">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED_VALUE}>Unassigned</SelectItem>
                {assignees.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.email ?? "(no email)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Due date">
            {/* Commit on blur, not on change: a date input fires `change` with
                "" while its segments are half-typed, and each patch refetches,
                flips the key and remounts the input mid-edit. Column and input
                speak the same yyyy-MM-dd, so the value passes through raw —
                `new Date(...)` would pin it to UTC midnight and shift the day
                for anyone west of UTC. */}
            <Input
              key={task.due_date ?? "none"}
              type="date"
              defaultValue={task.due_date ?? ""}
              aria-label="Due date"
              onBlur={(e) => {
                const next = e.target.value || null;
                if (next !== task.due_date) onPatch({ due_date: next });
              }}
            />
          </Field>
        </div>

        <Field label="Description">
          <Textarea
            key={task.description ?? "empty"}
            defaultValue={task.description ?? ""}
            rows={10}
            maxLength={5000}
            placeholder="Context, acceptance criteria, links…"
            onBlur={(e) => {
              const next = e.target.value.trim() || null;
              if (next !== task.description) onPatch({ description: next });
            }}
          />
        </Field>

        <dl className="border-border/60 text-muted-foreground grid grid-cols-2 gap-y-2 border-t pt-4 text-[11px]">
          <Meta label="Assigned to" value={assignee?.email ?? "Unassigned"} />
          <Meta label="Created by" value={createdBy?.email ?? "—"} />
          <Meta label="Created" value={format(new Date(task.created_at), "d MMM yyyy, HH:mm")} />
          <Meta
            label="Last updated"
            value={format(new Date(task.updated_at), "d MMM yyyy, HH:mm")}
          />
        </dl>

        <div className="flex justify-end pt-2">
          {confirmingDelete ? (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-[11px]">Delete this task?</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setConfirmingDelete(false)}
              >
                Cancel
              </Button>
              <Button type="button" variant="destructive" size="sm" onClick={onDelete}>
                Delete
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-destructive"
              onClick={() => setConfirmingDelete(true)}
            >
              <Trash2 className="size-3.5" /> Delete task
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="font-data text-[9px] tracking-[0.14em] uppercase opacity-70">{label}</dt>
      <dd className="text-foreground/80">{value}</dd>
    </div>
  );
}
